import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useIstToday } from "../lib/istDate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userPages } from "../lib/pages";
import DashLoading from "../components/DashLoading";
import { DashHero, DashCaption, Section, Tile, TileGrid, DashTable, useDashImage, n } from "../components/TempleDash";
import { TemplePicker, VerifyDialog, ValidityChip, StatusChip, EventNameEditor } from "../components/IdentityKit";
import { ID_KINDS, recName } from "../lib/identity";
import { CheckCircle2, BadgeCheck, Car, Baby, UserRound, Hourglass, ShieldCheck, CalendarClock, CalendarX2, ScanLine, ClipboardCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const AX = { fontSize: 11, fill: "#64748B" };
const ICON = { duty: BadgeCheck, vehicle: Car, children: Baby, pandit: UserRound };
const COLOR = { duty: "#1E3A8A", vehicle: "#0E7490", children: "#E8590C", pandit: "#15803D" };

export default function IdentityDashboard() {
  const { user } = useAuth();
  const pages = userPages(user);
  const isSA = user.role === "SUPER_ADMIN";
  const [templeId, setTempleId] = useState("");
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [verify, setVerify] = useState(false);
  const { url, temple, temples } = useDashImage("IDENTITY", templeId, user);

  const load = useCallback(() => {
    api.get("/identity/dashboard", { params: templeId ? { temple_id: templeId } : {} }).then((r) => { setD(r.data); setError(null); }).catch((e) => setError(e));
  }, [templeId]);
  const istDay = useIstToday(); // new day at 00:00 IST: reload today's figures at once
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load, istDay]);

  const k = d?.kinds || {};
  const pending = ["duty", "vehicle", "pandit"].reduce((s, x) => s + (k[x]?.pending || 0), 0);
  const validToday = Object.values(k).reduce((s, x) => s + (x.valid_today || 0), 0);
  const expiring = Object.values(k).reduce((s, x) => s + (x.expiring || 0), 0);

  return (
    <div className="space-y-4" data-testid="id-dashboard">
      <DashHero image={url} testid="id-hero"
        left={d ? [
          { icon: BadgeCheck, label: "Duty Passes Today", value: n(k.duty.today), sub: k.duty.pending ? `${k.duty.pending} pending` : "", testid: "idd-duty" },
          { icon: Car, label: "Vehicle Passes Today", value: n(k.vehicle.today), sub: k.vehicle.pending ? `${k.vehicle.pending} pending` : "", testid: "idd-vehicle" },
          { icon: Baby, label: "Children Tags Today", value: n(k.children.today), sub: "", testid: "idd-children" },
          { icon: UserRound, label: "Pandits Today", value: n(k.pandit.today), sub: k.pandit.pending ? `${k.pandit.pending} pending` : "", testid: "idd-pandit" },
        ] : []}
        right={d ? [
          { icon: ShieldCheck, label: "Valid Today", value: n(validToday), testid: "idd-valid" },
          { icon: Hourglass, label: "Pending (Today)", value: n(pending), testid: "idd-pending" },
          { icon: CalendarClock, label: "Expiring in 3 days", value: n(expiring), testid: "idd-expiring" },
          { icon: CalendarX2, label: "Registered Today", value: n(Object.values(k).reduce((s, x) => s + (x.today || 0), 0)), testid: "idd-today" },
        ] : []} />

      <DashCaption temple={temple} title="Identity & Passes Dashboard">
        <TemplePicker user={user} temples={temples} value={templeId} onChange={setTempleId} allowAll />
        <button onClick={() => setVerify(true)} className="h-10 px-4 rounded-lg bg-[#0E7490] text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="idd-verify"><ScanLine className="w-4 h-4" /> Verify Pass / Tag</button>
        {pages.includes("ID_APPROVALS") && <Link to="/identity/approvals" className="h-10 px-4 rounded-lg bg-[#B0121F] text-white text-sm font-semibold inline-flex items-center gap-1.5"><ClipboardCheck className="w-4 h-4" /> Approvals{pending ? ` (${pending})` : ""}</Link>}
      </DashCaption>

      {pages.includes("ID_MASTERS") && <EventNameEditor templeId={templeId || user.temple_id} temples={temples} />}

      {!d ? <DashLoading error={error} onRetry={() => { setError(null); load(); }} testid="idd-loading" /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(ID_KINDS).map((kind) => {
              const x = k[kind]; const Icon = ICON[kind];
              return (
                <Section key={kind} title={ID_KINDS[kind].plural}>
                  <TileGrid cols={4}>
                    <Tile icon={Icon} label="Today" value={n(x.today)} tone="blue" />
                    {kind === "children"
                      ? <Tile icon={Hourglass} label="Expiring 3d" value={n(x.expiring)} tone="yellow" />
                      : <Tile icon={Hourglass} label="Pending" value={n(x.pending)} tone="yellow" />}
                    <Tile icon={ShieldCheck} label="Valid Today" value={n(x.valid_today)} tone="green" />
                    <Tile icon={CheckCircle2} label="Approved Today" value={n(kind === "children" ? x.today : x.approved_today)} tone="red" />
                  </TileGrid>
                </Section>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section title="Registrations - Last 7 Days" className="lg:col-span-2" bodyClass="p-3 h-72">
              <ResponsiveContainer>
                <BarChart data={d.last7} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D2" vertical={false} />
                  <XAxis dataKey="day" tick={AX} tickLine={false} />
                  <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#FDF2E9" }} /><Legend wrapperStyle={{ fontSize: 12 }} />
                  {Object.keys(ID_KINDS).map((kind) => <Bar key={kind} dataKey={kind} name={ID_KINDS[kind].plural} stackId="a" fill={COLOR[kind]} maxBarSize={38} isAnimationActive={false} />)}
                </BarChart>
              </ResponsiveContainer>
            </Section>
            <Section title="Location / Ghat-wise (Today)" bodyClass="p-3 max-h-72 overflow-y-auto">
              <DashTable head={["Location / Ghat", "Passes"]} rows={d.location_wise.map((x) => [x.name, n(x.value)])} empty="No passes yet" />
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            <Section title="Department-wise (Today)">
              <DashTable head={["Department", "Passes"]} rows={d.department_wise.map((x) => [x.name, n(x.value)])} empty="No passes yet" />
            </Section>
            <Section title="Today's Registrations" className="lg:col-span-2" bodyClass="p-3 overflow-x-auto">
              <DashTable testid="idd-latest" head={["Number", "Type", "Name", "Ghat", "Status", "Validity"]} align={["left", "left", "left", "left", "left", "left"]} empty="No registrations yet"
                rows={d.latest.map((r) => [<span key="n" className="font-mono text-xs">{r.kind === "children" ? r.tag_serial : r.pass_no}</span>, ID_KINDS[r.kind].label,
                  r.vehicle_number ? `${r.vehicle_number} · ${recName(r)}` : recName(r), r.location_name || "-", <StatusChip key="s" s={r.status} />, <ValidityChip key="v" v={r.validity} />])} />
            </Section>
          </div>
        </>
      )}
      <VerifyDialog open={verify} onClose={() => setVerify(false)} temples={temples} />
    </div>
  );
}
